/**
 * O campo da foto da carteirinha no formulário do plano.
 *
 * Papel: mostra a foto gravada ou a escolhida, lê o arquivo escolhido ou
 * arrastado, valida-o com `novaFotoDaCarteirinhaSchema` e devolve ao
 * formulário o que deverá acontecer com a foto no "Salvar" — nada, enviar a
 * nova ou excluir a gravada —, em `EstadoDaFoto`.
 *
 * Como usar: o campo não grava nada. Quem o monta guarda o `EstadoDaFoto`, de
 * `types.ts`, começando por `FOTO_MANTIDA`, e só depois de gravar o plano chama o envio ou
 * a exclusão; uma foto enviada daqui, antes do plano, não teria apólice onde
 * ficar no plano novo, e ficaria gravada no plano editado mesmo que a pessoa
 * desistisse no "Cancelar".
 *
 * Excluir a foto gravada só marca a exclusão, que se desfaz pelo "Desfazer"
 * até o "Salvar". Descartar a foto escolhida e ainda não gravada volta ao
 * estado de antes da escolha, inclusive à exclusão marcada, se havia uma.
 *
 * O nome mostrado sob a foto escolhida é o de `nomeDoArquivoDaCarteirinha`, a
 * mesma regra com que a API vai gravá-la, montado com o titular e o plano que
 * estão no formulário naquele momento — mudar o titular muda o nome.
 */
import {
  type PlanoSaude,
  diaNoEscritorio,
  nomeDoArquivoDaCarteirinha,
} from '@concierge/shared';
import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';

import { formatarData } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { lerFotoDaCarteirinha } from '../foto-da-carteirinha';
import { useFotoDaCarteirinha } from '../hooks/usar-foto-da-carteirinha';
import { type EstadoDaFoto, FOTO_MANTIDA } from '../types';

interface CampoFotoDaCarteirinhaProps {
  /** A apólice gravada, de onde vem a foto atual, ou `null` no plano novo. */
  gravado: PlanoSaude | null;
  estado: EstadoDaFoto;
  onMudar: (estado: EstadoDaFoto) => void;
  /** O nome completo do titular escolhido no formulário, ou vazio. */
  nomeCompleto: string;
  /** O texto do campo `plano` no formulário. */
  plano: string;
  desabilitado: boolean;
}

/**
 * O campo da foto, com a prévia, o nome do arquivo e os botões.
 *
 * @param gravado - A apólice gravada; a foto dela aparece enquanto `estado`
 *   for `mantida`.
 * @param estado - O que está marcado para a foto no "Salvar".
 * @param onMudar - Chamado com o estado novo a cada escolha, descarte,
 *   exclusão ou "Desfazer".
 * @param nomeCompleto - O titular escolhido, para o nome da foto nova.
 * @param plano - O plano digitado, para o tipo no nome da foto nova.
 * @param desabilitado - Verdadeiro durante a gravação, quando nada se escolhe.
 * @returns O campo inteiro, com o rótulo e o erro de leitura.
 */
export function CampoFotoDaCarteirinha({
  gravado,
  estado,
  onMudar,
  nomeCompleto,
  plano,
  desabilitado,
}: CampoFotoDaCarteirinhaProps) {
  const [erro, setErro] = useState<string | null>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const fotoGravada = useFotoDaCarteirinha(gravado);
  const foto = gravado?.foto ?? null;

  /**
   * Lê o arquivo como data URL e, se passar no schema, marca-o como foto nova.
   *
   * @param arquivo - O arquivo escolhido no seletor ou solto sobre a prévia.
   */
  function lerArquivo(arquivo: File) {
    const anterior = estado.tipo === 'nova' ? estado.anterior : estado.tipo;
    void lerFotoDaCarteirinha(arquivo).then((leitura) => {
      if (!leitura.ok) {
        setErro(leitura.mensagem);
        return;
      }
      setErro(null);
      onMudar({
        tipo: 'nova',
        dataUrl: leitura.dataUrl,
        extensao: leitura.extensao,
        instante: new Date(),
        anterior,
      });
    });
  }

  /** Lê o arquivo escolhido no seletor e o esvazia, para a mesma escolha valer de novo. */
  function handleEscolher(evento: ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.currentTarget.files?.[0];
    evento.currentTarget.value = '';
    if (arquivo !== undefined) lerArquivo(arquivo);
  }

  /** Deixa a prévia receber o arquivo arrastado. */
  function handleArrastarSobre(evento: DragEvent<HTMLLabelElement>) {
    evento.preventDefault();
  }

  /** Lê o arquivo solto sobre a prévia. */
  function handleSoltar(evento: DragEvent<HTMLLabelElement>) {
    evento.preventDefault();
    if (desabilitado) return;
    const arquivo = evento.dataTransfer.files[0];
    if (arquivo !== undefined) lerArquivo(arquivo);
  }

  /** Abre o seletor de arquivo, como o clique na prévia. */
  function handleTrocar() {
    seletor.current?.click();
  }

  /** Descarta a foto escolhida, ou marca a exclusão da gravada. */
  function handleExcluir() {
    setErro(null);
    onMudar(estado.tipo === 'nova' ? { tipo: estado.anterior } : { tipo: 'excluida' });
  }

  /** Desfaz a exclusão marcada, e a foto gravada volta a valer. */
  function handleDesfazer() {
    setErro(null);
    onMudar(FOTO_MANTIDA);
  }

  const mostraGravada = estado.tipo === 'mantida' && foto !== null;
  const imagem =
    estado.tipo === 'nova' ? estado.dataUrl : mostraGravada ? fotoGravada.url : null;

  let textoDaPrevia: string | null = null;
  if (estado.tipo === 'excluida') textoDaPrevia = 'A foto será excluída ao salvar.';
  else if (mostraGravada && fotoGravada.falhou)
    textoDaPrevia = 'Não foi possível carregar a foto.';
  else if (!mostraGravada && estado.tipo === 'mantida')
    textoDaPrevia = 'Clique ou arraste a foto da carteirinha';

  return (
    <div className="col-span-full flex flex-col gap-1.5">
      <Label htmlFor="plano-foto">Foto da carteirinha</Label>
      <label
        onDragOver={handleArrastarSobre}
        onDrop={handleSoltar}
        className={cn(
          'flex h-[150px] w-[240px] cursor-pointer items-center justify-center overflow-hidden rounded-md border border-linha-forte p-1 text-center font-mono text-[10.5px] leading-snug text-texto-fraco transition-[border-color,color] duration-150 ease-in-out hover:border-brass hover:text-brass-light',
          imagem === null ? 'border-dashed' : 'border-solid',
        )}
      >
        <input
          ref={seletor}
          id="plano-foto"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          disabled={desabilitado}
          onChange={handleEscolher}
        />
        {imagem !== null ? (
          <img
            src={imagem}
            alt="Foto da carteirinha"
            className="h-full w-full object-contain"
          />
        ) : (
          textoDaPrevia !== null && <span>{textoDaPrevia}</span>
        )}
      </label>

      {estado.tipo === 'nova' && (
        <p className="font-mono text-[11px] text-texto-fraco">
          {nomeDoArquivoDaCarteirinha({
            nomeCompleto,
            plano,
            instante: estado.instante,
            extensao: estado.extensao,
          })}
        </p>
      )}
      {mostraGravada && (
        <div className="font-mono text-[11px] text-texto-fraco">
          <p>{foto.nome}</p>
          <p>Atualizada em {formatarData(diaNoEscritorio(new Date(foto.atualizadaEm)))}</p>
        </div>
      )}

      {(estado.tipo === 'nova' || mostraGravada) && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={desabilitado}
            onClick={handleTrocar}
          >
            Trocar foto
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-perigo"
            disabled={desabilitado}
            onClick={handleExcluir}
          >
            Excluir foto
          </Button>
        </div>
      )}
      {estado.tipo === 'excluida' && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={desabilitado}
            onClick={handleDesfazer}
          >
            Desfazer
          </Button>
        </div>
      )}

      {erro !== null && (
        <p className="text-sm text-destructive" role="alert">
          {erro}
        </p>
      )}
    </div>
  );
}
